import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import MobileMenu from "../components/MobileMenu";
import Overlay from "../components/Overlay";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { Registration, Profile, Medal, Record } from "../type";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { motion, useAnimation } from "motion/react";

const rolelabel = {
    admin: 'ادمین',
    coach: 'مربی',
    athlete: 'ورزشکار',
}

export default function ProfilePage() {
    const { isAuthenticated, accessToken, loading: loadingauth } = useAuth();
    const navigate = useNavigate();

    const [profile, setProfile] = useState<Profile>();
    const [walletBalance, setWalletBalance] = useState(0);
    const [registrations, setRegistrations] = useState<Registration[]>();
    const [medals, setMedals] = useState<Medal[]>();
    const [records, setRecords] = useState<Record>();

    const [loading, setLoading] = useState(true);

    const [menuOpen, setMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("profile");

    const containerRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const controls = useAnimation();
    const [maxDrag, setMaxDrag] = useState(0);

    const medalsContainerRef = useRef<HTMLDivElement>(null);
    const medalsSliderRef = useRef<HTMLDivElement>(null);
    const medalsControls = useAnimation();
    const [medalsMaxDrag, setMedalsMaxDrag] = useState(0);

    const [profileImage, setProfileImage] = useState<any>();

    const fetchdata = async () => {
        try {
            const mainRes = await api.get("/registrations", {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            const WalletRes = await api.get("/wallet/balance", {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            const BadgeRes = await api.get("/badges", {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            const profileRes = await api.get("/profile", {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            const RecordsRes = await api.get("/records", {
                headers: {
                    Authorization: accessToken
                        ? `Bearer ${accessToken}`
                        : "",
                },
            })

            if (mainRes.data?.data) {
                setRegistrations(mainRes.data.data);
            }
            if (WalletRes.data?.balance) {
                setWalletBalance(WalletRes.data.balance);
            }
            if (profileRes.data?.data) {
                setProfile(profileRes.data.data);
            }
            if (BadgeRes.data?.data) {
                setMedals(BadgeRes.data.data);
            }
            if (RecordsRes.data?.data) {
                setRecords(RecordsRes.data.data);
            }
        } catch (err) {
            console.log("data fetch error:", err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (loadingauth) return;

        if (!isAuthenticated) {
            navigate('/');
            return;
        }
        fetchdata();
    }, [isAuthenticated, loadingauth]);



    const isDraggable = useMemo(() => registrations ? registrations.length > 2 : false, [registrations]);
    const isMedalsDraggable = useMemo(() => medals ? medals.length > 3 : false, [medals]);

    useEffect(() => {
        if (isDraggable) {
            const container = containerRef.current;
            const slider = sliderRef.current;
            if (container && slider) {
                const containerWidth = container.offsetWidth;
                const sliderWidth = slider.scrollWidth;
                const dragLimit = sliderWidth - containerWidth;
                setMaxDrag(dragLimit > 0 ? dragLimit : 0);
                controls.set({ x: 0 });
            }
        }
    }, [registrations, controls, isDraggable]);

    useEffect(() => {
        if (isMedalsDraggable) {
            const container = medalsContainerRef.current;
            const slider = medalsSliderRef.current;
            if (container && slider) {
                const containerWidth = container.offsetWidth;
                const sliderWidth = slider.scrollWidth;
                const dragLimit = sliderWidth - containerWidth;
                setMedalsMaxDrag(dragLimit > 0 ? dragLimit : 0);
                medalsControls.set({ x: 0 });
            }
        }
    }, [medals, medalsControls, isMedalsDraggable]);

    const formatPersianDate = (dateString: string): string | undefined => {
        if (!dateString) return undefined;

        const date = new Date(dateString);


        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return undefined;
        }

        try {
            return new Intl.DateTimeFormat('fa-IR', {
                calendar: 'persian',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }).format(date);
        } catch (err) {
            console.warn('Intl persian calendar not supported, falling back:', err);
            try {
                return new Intl.DateTimeFormat('fa-IR', {
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                }).format(date);
            } catch {
                return date.toLocaleDateString();
            }
        }
    };

    const processRegistrationsForCount = (regList: Registration[]): Array<{
        name: string;
        date: string;
        count: number;
        image_url: string;
        originalDate: string;
    }> => {

        const registrationCounts = regList.reduce<{
            [key: string]: {
                count: number;
                firstItemDetails: Registration;
                date: string;
            }
        }>((acc, item) => {
            const Date = (item.date);
            const compositeKey = `${item.name}|${Date}`;

            if (!acc[compositeKey]) {
                acc[compositeKey] = {
                    count: 0,
                    firstItemDetails: item,
                    date: Date
                };
            }
            acc[compositeKey].count += 1;
            return acc;
        }, {});

        const processedArray = Object.entries(registrationCounts).map(([compositeKey, data]) => {
            return {
                name: data.firstItemDetails.name,
                date: data.date,
                count: data.count,
                image_url: data.firstItemDetails.image,
                originalDate: data.firstItemDetails.date
            };
        });

        return processedArray;
    };

    return (
        <div className="min-h-dvh bg-darkbg">
            <div dir="rtl"
                className="w-full max-w-[450px] mx-auto flex flex-col min-h-dvh max-h-dvh relative overflow-hidden font-yekan text-white">

                <Overlay active={menuOpen} close={() => setMenuOpen(false)} />
                <MobileMenu active={menuOpen} />
                <Header menuOpen={menuOpen} close={() => setMenuOpen(!menuOpen)} />

                <main className="px-4 pt-3 space-y-5 flex-1 overflow-y-auto pb-[70px] no-scrollbar">

                    <section
                        // className="relative overflow-hidden rounded-3xl border border-primary/20 
                        // bg-gradient-to-l from-black/5 to-black/50 p-4 shadow-lg shadow-black/20">
                        className="relative overflow-hidden rounded-3xl p-4 ">
                        {/* <div className="absolute -top-10 -left-10 w-28 h-28 bg-primary/10 blur-3xl rounded-full" /> */}
                        <div className="relative flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div
                                    onClick={() => navigate("/userinfo")}
                                    className="relative w-24 h-24 rounded-full cursor-pointer shrink-0"
                                >
                                    <img
                                        src={profile?.avatar || "../assets/img/avatar.png"}
                                        alt={profile?.full_name}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                    <div className="absolute inset-0 rounded-full ring-4 ring-primary" />
                                </div>

                                <div className="">
                                    <h1
                                        onClick={() => navigate("/userinfo")}
                                        className="text-primary text-2xl font-bold cursor-pointer"
                                    >
                                        {profile?.full_name}
                                    </h1>
                                    <p className="text-white/60 text-sm">{rolelabel[profile?.role as 'admin' | 'coach' | 'athlete']}</p>

                                    <button
                                        onClick={() => navigate("/userinfo")}
                                        className="mt-2 px-3 py-1.5 rounded-xl bg-primary text-black text-sm font-bold"
                                    >
                                        مشاهده پروفایل
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        // className="rounded-2xl border border-primary/20 bg-black/30 backdrop-blur-md p-4">
                        className="rounded-2xl p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div
                                onClick={() => navigate("/wallet/charge")}
                                className="flex items-center gap-3 cursor-pointer"
                            >
                                <div className="w-11 h-11 rounded-2xl bg-primary/15 flex items-center justify-center">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-6 h-6 stroke-primary fill-none"
                                        strokeWidth="2"
                                    >
                                        <path d="M3 7h15a3 3 0 013 3v6a3 3 0 01-3 3H3z" />
                                        <path d="M3 7l12-4v4" />
                                        <circle cx="17" cy="13" r="1.5" className="fill-primary stroke-none" />
                                    </svg>
                                </div>

                                <div>
                                    <p className="text-white/50 text-sm">موجودی کیف پول</p>
                                    <span className="text-primary font-bold text-lg">
                                        {walletBalance?.toLocaleString("fa")} تومان
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate("/wallet/charge")}
                                className="px-4 py-2 rounded-xl bg-primary text-black font-bold text-sm"
                            >
                                افزایش موجودی
                            </button>
                        </div>
                    </section>

                    <section className="grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-black/25 border border-white/5 p-4 text-center">
                            <p className="text-white/50 text-sm mb-2">پیمایش کیلومتر</p>
                            <h3 className="text-primary text-xl font-bold">{records?.distance_km || '0'}</h3>
                        </div>

                        <div className="rounded-2xl bg-black/25 border border-white/5 p-4 text-center">
                            <p className="text-white/50 text-sm mb-2">پیمایش ارتفاع</p>
                            <h3 className="text-primary text-xl font-bold">{records?.elevation_m || '0'}</h3>
                        </div>
                    </section>

                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h2 className="text-primary text-lg font-bold">مدال‌ها</h2>
                            {medals && <span className="text-white/40 text-sm">{medals.length} مدال</span>}
                        </div>

                        {medals && medals.length > 0 ? (
                            <div ref={medalsContainerRef} className="overflow-hidden pb-2">
                                <motion.div
                                    ref={medalsSliderRef}
                                    drag={isMedalsDraggable ? "x" : false}
                                    dragConstraints={{ left: 0, right: medalsMaxDrag }}
                                    dragElastic={0.12}
                                    whileTap={isMedalsDraggable ? { cursor: "grabbing" } : {}}
                                    animate={medalsControls}
                                    initial={false}
                                    className={`flex gap-3 ${isMedalsDraggable ? "w-max" : "flex-wrap justify-center"
                                        }`}
                                >
                                    {medals.map((medal) => (
                                        <div
                                            key={medal.id}
                                            // className="relative min-w-[120px] rounded-2xl p-3 border bg-black/20 border-primary/20 select-none"
                                            className="relative min-w-[120px] rounded-2xl p-3 select-none"
                                        >
                                            <img
                                                src={medal.image_url}
                                                alt={medal.name}
                                                className="w-16 h-16 object-contain mx-auto mb-2 pointer-events-none"
                                            />
                                            <h4 className="text-sm font-bold text-center">{medal.name}</h4>
                                            <p className="text-xs text-white/50 text-center mt-1">
                                                {medal.description}
                                            </p>
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-primary/20 bg-black/10 p-6 text-center text-white/50">
                                هنوز مدالی ثبت نشده است.
                            </div>
                        )}
                    </section>

                    <section className="space-y-3">
                        <h2 className="text-primary text-lg font-bold select-none">تاریخچه</h2>

                        {registrations && registrations.length > 0 ? (
                            <div ref={containerRef} className="overflow-hidden pb-4">
                                <motion.div
                                    ref={sliderRef}
                                    drag={isDraggable ? "x" : false}
                                    dragConstraints={{ left: 0, right: maxDrag }}
                                    dragElastic={0.15}
                                    whileTap={isDraggable ? { cursor: "grabbing" } : {}}
                                    animate={controls}
                                    initial={false}
                                    className={`flex gap-3 ${isDraggable ? "w-max" : "flex-wrap justify-center"
                                        }`}
                                >
                                    {processRegistrationsForCount(registrations).map((item, index) => (
                                        <div
                                            key={index}
                                            className="relative h-[120px] min-w-[210px] overflow-hidden rounded-2xl bg-[#1c1c1c] border border-white/10"
                                        >
                                            <img
                                                className="absolute inset-0 w-full h-full object-cover"
                                                src={item.image_url || "./assets/img/event1.png"}
                                                alt={item.name}
                                            />

                                            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

                                            <div className="relative z-10 h-full flex flex-col justify-end p-3">
                                                <h3 className="text-[16px] font-bold text-white line-clamp-1">
                                                    {item.name}
                                                </h3>
                                                <p className="text-[12px] text-white/75 mt-1">
                                                    {formatPersianDate(item.date)}
                                                </p>
                                                <div className="mt-2 inline-flex w-fit items-center rounded-lg bg-primary/80 px-2 py-1 text-[12px] text-black font-bold">
                                                    {((item.count).toLocaleString())} نفر
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-primary/20 bg-black/10 p-6 text-center text-white/50">
                                فعلاً موردی ثبت نشده است.
                            </div>
                        )}
                    </section>
                </main>

                <Footer activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
        </div>
    );
}